import type { ReactNode } from 'react';
import { workspaceHostDown, type WorkspaceHostState, type WorkspaceTargetState } from '@aioe/shared';
import { timeAgo } from '../hooks.ts';
import { StatusMark } from './ui.tsx';

type HostReport = { hostState?: WorkspaceHostState; distro?: string; targetState?: WorkspaceTargetState };

/** The words for a repository whose host is not running; nothing for a running one. */
function hostWords(r: HostReport): { state: 'offline' | 'working' | 'error'; label: string } | undefined {
  switch (r.hostState) {
    case 'stopped':
    case 'exited':
      return { state: 'offline', label: r.distro ? `Distro ${r.distro} stopped` : 'Host stopped' };
    case 'error':
      return { state: 'error', label: 'Host failed' };
    case 'starting':
    case 'provisioning':
      return { state: 'working', label: 'Starting' };
  }
  if (r.targetState === 'stopped') return { state: 'offline', label: 'Host stopped' };
  if (r.targetState === 'unavailable') return { state: 'offline', label: 'Unavailable' };
  return undefined;
}

/** A repository on a stopped WSL distro, dev container or unreachable host: a mark and when it last ran. */
export function HostStateMark({ root, lastSeenAt, otherwise = null }: { root: HostReport; lastSeenAt?: string; otherwise?: ReactNode }) {
  const words = hostWords(root);
  if (!words) return <>{otherwise}</>;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 text-xs text-muted">
      <StatusMark state={words.state} label={words.label} />
      {lastSeenAt && workspaceHostDown(root) && <span>last running {timeAgo(lastSeenAt)}</span>}
    </span>
  );
}
